import { useCallback, useEffect, useMemo, useState } from 'react';
import { bankingAPI, reconciliationAPI } from '../../api';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FormLabel } from '../ui/FormLabel';
import { Card, CardContent } from '../ui/Card';
import {
  ArrowLeftRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Search,
} from 'lucide-react';

/**
 * Transfer match modal (plan §6 / §8).
 *
 * Flow:
 *   1. Load this tenant's reconcilable accounts (excluding the source's own).
 *   2. User picks the target account the money moved to/from.
 *   3. We query /transfers/counterparts?source_statement_line_id=...&target_bank_account_id=...
 *      to find candidate lines on the target account (±7 days, same abs amount).
 *   4. User selects a candidate and confirms. Alternatively, if no candidate
 *      exists (or the counterpart side hasn't been imported yet) the user
 *      can park a pending-other-side transfer that will be auto-paired when
 *      the other side's statement arrives.
 *
 * Direction detection:
 *   - "source_side" radio lets the user override the default. Default is
 *     inferred from the sign of the source line: negative -> source is "from".
 */
function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function shortDate(d) {
  if (!d) return '—';
  return new Date(d).toISOString().slice(0, 10);
}

export function TransferMatchModal({
  open,
  onOpenChange,
  line,
  bankAccountId,
  onMatched,
}) {
  const lineAmount = Number(line?.amount ?? 0);
  const defaultSourceSide = lineAmount < 0 ? 'from' : 'to';

  const [accounts, setAccounts] = useState([]);
  const [accountsError, setAccountsError] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const [targetAccountId, setTargetAccountId] = useState('');
  const [sourceSide, setSourceSide] = useState(defaultSourceSide);
  const [transferDate, setTransferDate] = useState('');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');

  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);

  const [parkPending, setParkPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const resetFormState = useCallback(() => {
    setTargetAccountId('');
    setSourceSide(lineAmount < 0 ? 'from' : 'to');
    setTransferDate(line?.transaction_date ? shortDate(line.transaction_date) : '');
    setReference(line?.reference || '');
    setDescription('');
    setCandidates([]);
    setSelectedCandidateId(null);
    setParkPending(false);
    setCandidateError('');
    setError('');
    setDone(null);
    setBusy(false);
  }, [line?.transaction_date, line?.reference, lineAmount]);

  useEffect(() => {
    if (!open) return;
    resetFormState();
  }, [open, line?.id, resetFormState]);

  // Load reconcilable bank accounts (excluding source).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingAccounts(true);
        setAccountsError('');
        const list = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        const filtered = (list || []).filter(
          (a) => a.id !== bankAccountId && a.is_reconcilable !== false
        );
        setAccounts(filtered);
      } catch (err) {
        if (cancelled) return;
        setAccountsError(err?.response?.data?.error || err.message || 'Could not load accounts');
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, bankAccountId]);

  // Whenever target account changes, reload candidate counterparts.
  useEffect(() => {
    if (!open || !line?.id || !targetAccountId) {
      setCandidates([]);
      setSelectedCandidateId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingCandidates(true);
        setCandidateError('');
        const res = await reconciliationAPI.findTransferCounterparts({
          source_statement_line_id: line.id,
          target_bank_account_id: targetAccountId,
          window_days: 7,
          limit: 20,
        });
        if (cancelled) return;
        setCandidates(res.candidates || []);
        if ((res.candidates || []).length > 0) {
          setSelectedCandidateId(res.candidates[0].id);
          setParkPending(false);
        } else {
          setSelectedCandidateId(null);
          setParkPending(true);
        }
      } catch (err) {
        if (cancelled) return;
        setCandidateError(err?.response?.data?.error || err.message || 'Could not find counterparts');
        setCandidates([]);
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, line?.id, targetAccountId]);

  const canSubmit = useMemo(() => {
    if (!line || !bankAccountId || !targetAccountId) return false;
    if (parkPending) return true;
    return !!selectedCandidateId;
  }, [line, bankAccountId, targetAccountId, parkPending, selectedCandidateId]);

  const submit = async () => {
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      const body = {
        source_statement_line_id: line.id,
        source_side: sourceSide,
        transfer_date: transferDate || undefined,
        reference: reference || null,
        description: description || null,
      };
      if (parkPending) {
        body.target_bank_account_id = targetAccountId;
      } else {
        body.counterpart_statement_line_id = selectedCandidateId;
      }

      const result = await reconciliationAPI.createTransfer(body);
      setDone({
        transfer: result.transfer,
        pending: result.transfer?.status === 'pending_other_side',
      });
      if (onMatched) onMatched(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not create transfer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Match internal transfer">
      {!line ? (
        <div className="text-xs text-gray-500">No statement line selected.</div>
      ) : (
        <div className="space-y-4">
          {/* Source line summary */}
          <Card>
            <CardContent className="p-3 text-xs">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <ArrowLeftRight className="h-3.5 w-3.5" />
                Source line (this account)
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Date</div>
                  <div>{shortDate(line.transaction_date)}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Description</div>
                  <div className="truncate" title={line.description || ''}>
                    {line.description || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Reference</div>
                  <div>{line.reference || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Counterparty</div>
                  <div className="truncate">{line.counterparty || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Amount</div>
                  <div
                    className={
                      lineAmount >= 0
                        ? 'text-green-700 dark:text-green-400 font-semibold'
                        : 'text-red-700 dark:text-red-400 font-semibold'
                    }
                  >
                    {formatMoney(lineAmount)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {done ? (
            <Card
              className={
                done.pending
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
                  : 'border-green-500 bg-green-50 dark:bg-green-950/30'
              }
            >
              <CardContent className="p-4 text-xs">
                <div
                  className={
                    done.pending
                      ? 'flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-200'
                      : 'flex items-center gap-2 font-semibold text-green-800 dark:text-green-200'
                  }
                >
                  {done.pending ? (
                    <>
                      <Clock className="h-4 w-4" />
                      Transfer parked as pending other side.
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Transfer match created (draft).
                    </>
                  )}
                </div>
                {done.pending && (
                  <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">
                    When the matching line is imported on the other account, it will be
                    auto-paired (±7 days, same amount). You can also complete it manually from the transfers list.
                  </div>
                )}
                <div className="mt-3">
                  <Button size="sm" onClick={() => onOpenChange(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {error && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </CardContent>
                </Card>
              )}
              {accountsError && (
                <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                  <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{accountsError}</span>
                  </CardContent>
                </Card>
              )}

              {/* Target account picker */}
              <div>
                <FormLabel>Target account (other side)</FormLabel>
                <Select
                  value={targetAccountId}
                  onChange={(e) => setTargetAccountId(e.target.value)}
                  disabled={loadingAccounts}
                >
                  <option value="">
                    {loadingAccounts ? 'Loading accounts…' : 'Select target account…'}
                  </option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name}
                      {a.account_type ? ` — ${a.account_type.replace(/_/g, ' ')}` : ''}
                    </option>
                  ))}
                </Select>
                {!loadingAccounts && accounts.length === 0 && !accountsError && (
                  <div className="mt-1 text-[11px] text-gray-500">
                    No other reconcilable accounts exist. Create one first on the banking page.
                  </div>
                )}
              </div>

              {/* Direction override */}
              <div>
                <FormLabel>Direction</FormLabel>
                <div className="flex gap-4 text-xs">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="source-side"
                      value="from"
                      checked={sourceSide === 'from'}
                      onChange={() => setSourceSide('from')}
                    />
                    This line is the &quot;from&quot; side (money leaves this account)
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="source-side"
                      value="to"
                      checked={sourceSide === 'to'}
                      onChange={() => setSourceSide('to')}
                    />
                    This line is the &quot;to&quot; side (money arrives here)
                  </label>
                </div>
              </div>

              {/* Candidate counterparts */}
              {targetAccountId && (
                <div>
                  <FormLabel>
                    <span className="inline-flex items-center gap-1">
                      <Search className="h-3 w-3" />
                      Counterpart line on target account (±7 days, matching amount)
                    </span>
                  </FormLabel>
                  {loadingCandidates ? (
                    <div className="text-xs text-gray-500">Searching for counterparts…</div>
                  ) : candidateError ? (
                    <Card className="border-red-400 bg-red-50 dark:bg-red-950/30">
                      <CardContent className="flex gap-2 p-3 text-xs text-red-700 dark:text-red-300">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span>{candidateError}</span>
                      </CardContent>
                    </Card>
                  ) : candidates.length === 0 ? (
                    <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                      <CardContent className="p-3 text-xs text-amber-800 dark:text-amber-200">
                        <div className="flex items-center gap-2 font-semibold">
                          <Clock className="h-3.5 w-3.5" />
                          No matching line found on the target account.
                        </div>
                        <div className="mt-1">
                          You can still park a pending transfer — the system will
                          auto-pair it when the counterpart is imported.
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="max-h-56 space-y-1 overflow-auto rounded border border-gray-200 p-1 dark:border-gray-700">
                      {candidates.map((c) => {
                        const selected = selectedCandidateId === c.id;
                        return (
                          <label
                            key={c.id}
                            className={
                              'flex cursor-pointer items-start gap-2 rounded p-2 text-xs ' +
                              (selected
                                ? 'bg-blue-100 ring-1 ring-blue-400 dark:bg-blue-900/40'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800')
                            }
                          >
                            <input
                              type="radio"
                              name="transfer-counterpart"
                              className="mt-0.5"
                              checked={selected}
                              onChange={() => {
                                setSelectedCandidateId(c.id);
                                setParkPending(false);
                              }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="font-medium">
                                  {shortDate(c.transaction_date)} ·{' '}
                                  <span
                                    className={
                                      Number(c.amount) >= 0
                                        ? 'text-green-700 dark:text-green-400'
                                        : 'text-red-700 dark:text-red-400'
                                    }
                                  >
                                    {formatMoney(c.amount)}
                                  </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-wide text-gray-500">
                                  {c.account_name}
                                </div>
                              </div>
                              <div
                                className="mt-0.5 truncate text-gray-600 dark:text-gray-400"
                                title={c.description || ''}
                              >
                                {c.description || '—'}
                                {c.reference ? ` · ${c.reference}` : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <input
                      id="park-pending"
                      type="checkbox"
                      checked={parkPending}
                      onChange={(e) => {
                        setParkPending(e.target.checked);
                        if (e.target.checked) setSelectedCandidateId(null);
                        else if (candidates.length > 0) {
                          setSelectedCandidateId(candidates[0].id);
                        }
                      }}
                    />
                    <label htmlFor="park-pending">
                      Park as pending (other side not yet imported)
                    </label>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <FormLabel>Transfer date</FormLabel>
                  <Input
                    type="date"
                    value={transferDate}
                    onChange={(e) => setTransferDate(e.target.value)}
                  />
                </div>
                <div>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. TFR-2026-04-01"
                  />
                </div>
              </div>

              <div>
                <FormLabel>Description / memo</FormLabel>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional note"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button onClick={submit} disabled={!canSubmit || busy}>
                  {busy
                    ? 'Saving…'
                    : parkPending
                      ? 'Park pending transfer'
                      : 'Create transfer match'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </SlideOver>
  );
}

export default TransferMatchModal;
